import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";

export const MessageThread = ({ bookingId }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/messages`);
      setMessages(data);
    } catch (e) {
      if (e.response?.status === 403) setDisabled(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/bookings/${bookingId}/messages`, { body });
      setBody("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (disabled) {
    return (
      <p className="text-sm font-medium text-midnight-black/60" data-testid="messaging-disabled-notice">
        Messaging is currently turned off.
      </p>
    );
  }

  return (
    <div data-testid="message-thread">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-extrabold uppercase text-sm">Messages</h3>
        <button onClick={load} data-testid="refresh-messages-button" className="text-midnight-black/50 hover:text-stranger-orange" aria-label="Refresh messages">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="border border-soft-gray rounded-xl p-4 max-h-64 overflow-y-auto mb-3 space-y-3" data-testid="messages-list">
        {messages === null ? (
          <p className="text-xs font-medium text-midnight-black/50">Loading...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs font-medium text-midnight-black/50" data-testid="no-messages">
            No messages yet.
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`text-sm font-medium ${m.sender_id === user.id ? "text-right" : "text-left"}`} data-testid={`message-${m.id}`}>
              <span className={`inline-block px-3 py-2 rounded-xl max-w-[80%] ${m.sender_id === user.id ? "bg-stranger-orange text-clean-white" : "bg-soft-gray text-midnight-black"}`}>{m.body}</span>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <Textarea data-testid="message-input" placeholder="Write a message..." value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[44px]" />
        <Button data-testid="send-message-button" onClick={send} disabled={sending} className="bg-midnight-black text-clean-white font-bold rounded-full">
          SEND
        </Button>
      </div>
    </div>
  );
};
